import type { z } from 'zod';

/**
 * Minimal typed JSON client. Responses are validated against the shared contracts, so a contract
 * drift surfaces as an error rather than as undefined fields. Security is enforced by the API;
 * nothing here is a security control (SDD §22.4).
 */

/** A problem+json response (RFC 9457), as the API's error filter renders it (SDD §10.6). */
export interface ProblemDetails {
  readonly title?: string;
  readonly status?: number;
  readonly code?: string;
  readonly detail?: string;
  readonly errors?: readonly { field: string; rule: string; message: string }[];
  readonly correlationId?: string;
}

/** An API failure carrying whatever the problem document said, for display. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly problem: ProblemDetails | null,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** The field-level messages a 422 carries, ready to list under the main message. */
  get fieldErrors(): readonly string[] {
    return (this.problem?.errors ?? []).map((e) => `${e.field}: ${e.message}`);
  }
}

/**
 * The message to show for a failed request: the problem's own message followed by any field-level
 * reasons of a 422, so a refusal says why (e.g. which line exceeds its outstanding quantity).
 */
export function errorMessage(error: Error): string {
  if (error instanceof ApiError) {
    const errors = error.problem?.errors ?? [];
    if (errors.length > 0) return `${error.message}: ${errors.map((e) => e.message).join('; ')}`;
  }
  return error.message;
}

/** The API wraps record data as `{ data, meta? }` (SDD §12.2). */
export interface Envelope<T> {
  readonly data: T;
  readonly meta?: {
    readonly page: number;
    readonly pageSize: number;
    // eslint-disable-next-line no-restricted-syntax -- a row count, never a monetary total
    readonly total: number;
  };
}

/** Narrows the several shapes `HeadersInit` allows to the plain record the spread expects. */
function asHeaders(headers: HeadersInit | undefined): Record<string, string> {
  if (headers === undefined) return {};
  return headers instanceof Headers
    ? Object.fromEntries(headers.entries())
    : Array.isArray(headers)
      ? Object.fromEntries(headers)
      : headers;
}

async function readProblem(res: Response): Promise<ProblemDetails | null> {
  try {
    return (await res.json()) as ProblemDetails;
  } catch {
    return null;
  }
}

/** A message worth showing a person, preferring what the API said over a status code. */
function messageFor(res: Response, problem: ProblemDetails | null): string {
  if (res.status === 401) return 'Your session has ended. Sign in again to continue.';
  // A segregation-of-duties refusal says which earlier action conflicts and who may act instead
  // (FRD-USR-021), and a capability switched off by configuration says so (e.g. live invoicing,
  // SDD §8.12); every other 403 keeps the generic wording.
  if (
    res.status === 403 &&
    problem?.code !== undefined &&
    problem.code !== 'forbidden' &&
    (problem.detail ?? '') !== ''
  )
    return problem.detail ?? '';
  if (res.status === 403) return 'You do not have permission to do that.';
  const detail = problem?.detail ?? problem?.title;
  if (detail !== undefined && detail !== '') return detail;
  return `The request failed (${String(res.status)}).`;
}

async function request(
  path: string,
  init: RequestInit,
  acceptStatuses: readonly number[],
): Promise<Response> {
  const res = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: { accept: 'application/json', ...asHeaders(init.headers) },
  });
  if (!res.ok && !acceptStatuses.includes(res.status)) {
    const problem = await readProblem(res);
    throw new ApiError(messageFor(res, problem), res.status, problem);
  }
  return res;
}

export async function getJson<S extends z.ZodType>(
  path: string,
  schema: S,
  init: { acceptStatuses?: readonly number[] } = {},
): Promise<z.infer<S>> {
  const res = await request(path, { method: 'GET' }, init.acceptStatuses ?? []);
  return schema.parse(await res.json());
}

/**
 * An unsafe method, so it carries the CSRF token from `/api/v1/me` (SDD §11.3). The token is a
 * request credential, not an authorization decision: the API still checks the session and the
 * caller's permissions.
 */
export async function postJson<S extends z.ZodType>(
  path: string,
  body: unknown,
  schema: S,
  csrfToken: string,
  options: WriteOptions = {},
): Promise<z.infer<S>> {
  const res = await request(
    path,
    {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'X-CSRF-Token': csrfToken,
        ...writeHeaders(options),
      },
      body: JSON.stringify(body),
    },
    [],
  );
  const envelope = (await res.json()) as Envelope<unknown>;
  return schema.parse(envelope.data);
}

/**
 * A change to an existing record: CSRF like every unsafe method, and `If-Match` with the version
 * the caller saw, so a change based on stale data is refused rather than applied (SDD §7.3).
 * Without `expectedVersion`, for the few small items that carry no version (a project member's
 * role, a board column, a checklist item), no `If-Match` is sent.
 */
export async function patchJson<S extends z.ZodType>(
  path: string,
  body: unknown,
  schema: S,
  csrfToken: string,
  expectedVersion?: number,
): Promise<z.infer<S>> {
  const res = await request(
    path,
    {
      method: 'PATCH',
      headers: {
        'content-type': 'application/json',
        'X-CSRF-Token': csrfToken,
        ...writeHeaders({ expectedVersion }),
      },
      body: JSON.stringify(body),
    },
    [],
  );
  const envelope = (await res.json()) as Envelope<unknown>;
  return schema.parse(envelope.data);
}

/**
 * A PUT carrying the CSRF token: records a new value for a keyed resource, such as a configuration
 * parameter (each PUT is a new, audited row — SDD §15.2), or replaces a list under the owning
 * record's version (`expectedVersion` sends `If-Match`).
 */
export async function putJson<S extends z.ZodType>(
  path: string,
  body: unknown,
  schema: S,
  csrfToken: string,
  expectedVersion?: number,
): Promise<z.infer<S>> {
  const res = await request(
    path,
    {
      method: 'PUT',
      headers: {
        'content-type': 'application/json',
        'X-CSRF-Token': csrfToken,
        ...writeHeaders({ expectedVersion }),
      },
      body: JSON.stringify(body),
    },
    [],
  );
  const envelope = (await res.json()) as Envelope<unknown>;
  return schema.parse(envelope.data);
}

/**
 * A `multipart/form-data` POST — a document upload (SDD §10.8). CSRF like every unsafe method.
 * The browser sets the multipart boundary itself, so no content type is given here. The caller
 * appends the text fields before the file: the API authorizes an upload from its fields before it
 * stores a byte (SDD §17.4).
 */
export async function postForm<S extends z.ZodType>(
  path: string,
  form: FormData,
  schema: S,
  csrfToken: string,
  options: WriteOptions = {},
): Promise<z.infer<S>> {
  const res = await request(
    path,
    {
      method: 'POST',
      headers: { 'X-CSRF-Token': csrfToken, ...writeHeaders(options) },
      body: form,
    },
    [],
  );
  const envelope = (await res.json()) as Envelope<unknown>;
  return schema.parse(envelope.data);
}

/** A DELETE carrying the CSRF token, for the few resources that are removed (SDD §11.5). */
export async function deleteJson<S extends z.ZodType>(
  path: string,
  schema: S,
  csrfToken: string,
): Promise<z.infer<S>> {
  const res = await request(path, { method: 'DELETE', headers: { 'X-CSRF-Token': csrfToken } }, []);
  const envelope = (await res.json()) as Envelope<unknown>;
  return schema.parse(envelope.data);
}

/** A DELETE that answers 204 with no body, such as removing a project member (CLR-66). */
export async function deleteNoContent(path: string, csrfToken: string): Promise<void> {
  await request(path, { method: 'DELETE', headers: { 'X-CSRF-Token': csrfToken } }, []);
}

/**
 * The two headers the API requires on writes beyond CSRF (SDD §7.3, §7.5).
 *
 * - `idempotencyKey` — required on the creation of every financial document. The **caller** owns
 *   it: it must be generated once per deliberate submission and reused if that submission is
 *   retried, so a repeat returns the original document instead of creating a second one. It must
 *   never be regenerated by a React re-render.
 * - `expectedVersion` — the record's current version, sent as `If-Match`, so a change or a post
 *   is refused when someone else moved the record first.
 */
export interface WriteOptions {
  readonly idempotencyKey?: string | undefined;
  readonly expectedVersion?: number | undefined;
}

function writeHeaders(options: WriteOptions): Record<string, string> {
  const headers: Record<string, string> = {};
  if (options.idempotencyKey !== undefined) headers['Idempotency-Key'] = options.idempotencyKey;
  if (options.expectedVersion !== undefined) {
    headers['If-Match'] = `"${String(options.expectedVersion)}"`;
  }
  return headers;
}

/** A fresh idempotency key for one deliberate submission (SDD §7.5). */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

/** Reads `{ data }` and validates the payload inside it. */
export async function getData<S extends z.ZodType>(path: string, schema: S): Promise<z.infer<S>> {
  const res = await request(path, { method: 'GET' }, []);
  const envelope = (await res.json()) as Envelope<unknown>;
  return schema.parse(envelope.data);
}

/** Reads `{ data, meta }` for a paged list. */
export async function getPage<S extends z.ZodType>(
  path: string,
  schema: S,
  // eslint-disable-next-line no-restricted-syntax -- a row count, never a monetary total
): Promise<{ items: z.infer<S>[]; total: number }> {
  const res = await request(path, { method: 'GET' }, []);
  const envelope = (await res.json()) as Envelope<unknown[]>;
  return {
    items: envelope.data.map((item) => schema.parse(item)),
    total: envelope.meta?.total ?? envelope.data.length,
  };
}
